import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, take } from 'rxjs';
import { 
  BackgroundCheckService, 
  BackgroundCheckPackage,
  InitiateBackgroundCheckResponse 
} from '../../services/background-check.service';

export interface BackgroundCheckDialogData {
  jobApplicationId: number;
  candidateName: string;
  candidateEmail: string;
}

export interface BackgroundCheckDialogResult {
  success: boolean;
  status?: string;
  error?: string;
}

type DialogState = 'loading' | 'package-selection' | 'processing' | 'success' | 'error' | 'already-exists';

@Component({
  selector: 'app-background-check-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatDialogModule],
  template: `
    <div class="bg-white rounded-xl max-w-md w-full mx-auto">
      <!-- Header -->
      <div class="p-4 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z">
              </path>
            </svg>
          </div>
          <div>
            <h2 class="text-lg font-semibold text-gray-900">
              {{ 'BACKGROUND_CHECK.TITLE' | translate }}
            </h2>
            <p class="text-sm text-gray-500">{{ data.candidateName }}</p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="p-6 overflow-y-auto flex-1">
        <!-- Loading State -->
        <div *ngIf="state === 'loading'" class="flex flex-col items-center py-8">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
          <p class="text-gray-600">{{ 'BACKGROUND_CHECK.LOADING' | translate }}</p>
        </div>

        <!-- Package Selection State -->
        <div *ngIf="state === 'package-selection'" class="space-y-3">
          <p class="text-sm text-gray-600 mb-3">
            Select a background check package for {{ data.candidateName }}
          </p>
          
          <div class="space-y-3">
            <div 
              *ngFor="let package of packages" 
              (click)="selectPackage(package)"
              [class.border-blue-500]="selectedPackage?.id === package.id"
              [class.bg-blue-50]="selectedPackage?.id === package.id"
              class="border-2 rounded-lg p-3 cursor-pointer hover:border-blue-300 transition-colors relative">
              
              <!-- Most Popular Badge -->
              <div 
                *ngIf="isRecommendedPackage(package)"
                class="absolute -top-2.5 left-3 bg-blue-600 text-white px-2.5 py-0.5 rounded-full text-xs font-semibold">
                MOST POPULAR
              </div>
              
              <div class="flex justify-between items-start mb-2">
                <div>
                  <h3 class="font-semibold text-gray-900">{{ package.name }}</h3>
                  <p class="text-2xl font-bold text-blue-600">\${{ package.priceUsd.toFixed(2) }}</p>
                </div>
                <div 
                  *ngIf="selectedPackage?.id === package.id"
                  class="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
              </div>
              
              <ul class="space-y-1">
                <li 
                  *ngFor="let feature of package.features" 
                  class="text-sm text-gray-600 flex items-start">
                  <svg class="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  {{ feature }}
                </li>
              </ul>

              <!-- Additional info for recommended package -->
              <div 
                *ngIf="isRecommendedPackage(package)"
                class="mt-2 pt-2 border-t border-gray-200">
                <p class="text-xs text-gray-600 flex items-start">
                  <svg class="w-3.5 h-3.5 text-blue-500 mr-1 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span>Verifies records directly at county courthouses for the most accurate and current results</span>
                </p>
              </div>
            </div>
          </div>
          
          <div class="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-4">
            <p class="text-sm text-yellow-700">
              Your saved payment method will be charged immediately upon confirmation.
            </p>
          </div>
        </div>

        <!-- Processing State -->
        <div *ngIf="state === 'processing'" class="flex flex-col items-center py-8">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
          <p class="text-gray-600">{{ 'BACKGROUND_CHECK.PROCESSING' | translate }}</p>
        </div>

        <!-- Success State -->
        <div *ngIf="state === 'success'" class="text-center py-8">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            {{ 'BACKGROUND_CHECK.SUCCESS_TITLE' | translate }}
          </h3>
          <p class="text-gray-600">
            {{ 'BACKGROUND_CHECK.SUCCESS_MESSAGE' | translate }}
          </p>
        </div>

        <!-- Error State -->
        <div *ngIf="state === 'error'" class="text-center py-8">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            {{ 'BACKGROUND_CHECK.ERROR_TITLE' | translate }}
          </h3>
          <p class="text-gray-600">{{ errorMessage }}</p>
        </div>

        <!-- Already Exists State -->
        <div *ngIf="state === 'already-exists'" class="text-center py-8">
          <div class="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            {{ 'BACKGROUND_CHECK.ALREADY_EXISTS_TITLE' | translate }}
          </h3>
          <p class="text-gray-600">
            {{ 'BACKGROUND_CHECK.ALREADY_EXISTS_MESSAGE' | translate }}
          </p>
          <p class="text-sm text-gray-500 mt-2">Status: {{ existingStatus }}</p>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="p-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
        <!-- Cancel Button -->
        <button 
          *ngIf="state === 'package-selection'"
          (click)="onCancel()"
          class="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          Cancel
        </button>

        <!-- Confirm & Pay Button -->
        <button 
          *ngIf="state === 'package-selection'"
          (click)="confirmPayment()"
          [disabled]="!selectedPackage"
          [class.opacity-50]="!selectedPackage"
          [class.cursor-not-allowed]="!selectedPackage"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:hover:bg-blue-600">
          Confirm & Pay \${{ (selectedPackage?.priceUsd ?? 0).toFixed(2) }}
        </button>

        <!-- Close Button (Success/Error/Already Exists) -->
        <button 
          *ngIf="state === 'success' || state === 'error' || state === 'already-exists'"
          (click)="onClose()"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          Close
        </button>

        <!-- Retry Button (Error only) -->
        <button 
          *ngIf="state === 'error'"
          (click)="onRetry()"
          class="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
          Retry
        </button>
      </div>
    </div>
  `,
  styles: []
})
export class BackgroundCheckDialogComponent implements OnInit, OnDestroy {
  state: DialogState = 'loading';
  packages: BackgroundCheckPackage[] = [];
  selectedPackage: BackgroundCheckPackage | null = null;
  errorMessage: string = '';
  existingStatus: string = '';
  private subscription = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<BackgroundCheckDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BackgroundCheckDialogData,
    private _backgroundCheckService: BackgroundCheckService
  ) {}

  ngOnInit(): void {
    this.loadPackages();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private loadPackages(): void {
    this.subscription.add(
      this._backgroundCheckService.getPackages().pipe(take(1)).subscribe({
        next: (response) => {
          this.packages = response.packages;
          // Auto-select the standard package as default
          this.selectedPackage = this.packages.find(pkg => pkg.id.toLowerCase() === 'standard') || null;
          this.state = 'package-selection';
        },
        error: (error) => {
          console.error('Error loading packages:', error);
          this.state = 'error';
          this.errorMessage = 'Failed to load package options';
        }
      })
    );
  }

  selectPackage(pkg: BackgroundCheckPackage): void {
    this.selectedPackage = pkg;
  }

  /**
   * Determines if a package should be marked as recommended/most popular
   * Currently identifies the 'standard' package as the recommended option
   */
  isRecommendedPackage(pkg: BackgroundCheckPackage): boolean {
    return pkg.id.toLowerCase() === 'standard';
  }

  confirmPayment(): void {
    if (!this.selectedPackage) return;

    this.state = 'processing';
    
    this.subscription.add(
      this._backgroundCheckService.initiateBackgroundCheck({
        jobApplicationId: this.data.jobApplicationId,
        packageType: this.selectedPackage.id
      }).pipe(take(1)).subscribe({
        next: (response) => {
          if (response.existingStatus) {
            this.existingStatus = response.existingStatus;
            this.state = 'already-exists';
            return;
          }

          if (!response.isSuccess) {
            this.errorMessage = response.errorMessage || 'Failed to initiate background check';
            this.state = 'error';
            return;
          }

          // Payment succeeded and background check initiated
          if (response.autoConfirmed) {
            this.state = 'success';
            return;
          }

          // Unexpected response
          this.errorMessage = 'Unexpected response from server';
          this.state = 'error';
        },
        error: (err) => {
          console.error('Error initiating background check:', err);
          this.errorMessage = err.error?.errorMessage || 'An error occurred while initiating the background check';
          this.state = 'error';
        }
      })
    );
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  onClose(): void {
    const success = this.state === 'success';
    this.dialogRef.close({ success, status: success ? 'InProgress' : undefined });
  }

  onRetry(): void {
    this.state = 'package-selection';
    this.errorMessage = '';
    this.selectedPackage = null;
  }
}